import {
  Badge,
  Button,
  Checkbox,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  makeStyles,
  Spinner,
  Subtitle2,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  tokens,
} from "@fluentui/react-components"
import {
  type CreateWebhookRequest,
  type CreateWebhookResponse,
  type TestWebhookResponse,
  WEBHOOK_EVENTS,
  type Webhook,
  type WebhookEventName,
  type WebhookPage,
} from "@rema/shared/schemas/webhooks"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { api, isApiError } from "../../lib/api"
import { problemMessage } from "../../lib/problem"
import { settingsI18n } from "./i18n"

const i18n = settingsI18n.webhooks

const useStyles = makeStyles({
  panel: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalL,
  },
  events: { display: "flex", flexWrap: "wrap", gap: tokens.spacingHorizontalM },
  mono: { fontFamily: tokens.fontFamilyMonospace, wordBreak: "break-all" },
})

const QUERY_KEY = ["admin", "webhooks"] as const

export const WebhooksPanel = () => {
  const styles = useStyles()
  const qc = useQueryClient()
  const [url, setUrl] = useState("")
  const [events, setEvents] = useState<Set<WebhookEventName>>(new Set())
  const [error, setError] = useState<string | null>(null)
  const [secret, setSecret] = useState<string | null>(null)
  const [testResult, setTestResult] = useState<{ ok: boolean; code: number | null } | null>(null)

  const onError = (e: unknown) =>
    setError(isApiError(e) ? problemMessage(e.problem) : settingsI18n.common.saveError)
  const invalidate = () => qc.invalidateQueries({ queryKey: QUERY_KEY })

  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => api.get<WebhookPage>("/admin/webhooks"),
  })
  const create = useMutation({
    mutationFn: (input: CreateWebhookRequest) =>
      api.post<CreateWebhookResponse>("/admin/webhooks", input),
    onSuccess: (res) => {
      setError(null)
      setSecret(res.secret)
      setUrl("")
      setEvents(new Set())
      invalidate()
    },
    onError,
  })
  const test = useMutation({
    mutationFn: (id: string) => api.post<TestWebhookResponse>(`/admin/webhooks/${id}/test`),
    onSuccess: (res) => setTestResult({ ok: res.delivered, code: res.status_code }),
    onError,
  })
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/webhooks/${id}`),
    onSuccess: invalidate,
    onError,
  })

  const toggle = (event: WebhookEventName, checked: boolean) =>
    setEvents((prev) => {
      const next = new Set(prev)
      if (checked) next.add(event)
      else next.delete(event)
      return next
    })

  if (query.isPending) return <Spinner />
  const hooks = query.data?.data ?? []
  return (
    <div className={styles.panel}>
      <Subtitle2>{i18n.title}</Subtitle2>
      <p>{i18n.description}</p>
      {error ? (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      ) : null}
      {secret ? (
        <MessageBar intent="success">
          <MessageBarBody>
            {i18n.secretNotice} <span className={styles.mono}>{secret}</span>
          </MessageBarBody>
        </MessageBar>
      ) : null}
      {testResult ? (
        <MessageBar intent={testResult.ok ? "success" : "warning"}>
          <MessageBarBody>
            {testResult.ok ? i18n.testOk : i18n.testFail}
            {testResult.code !== null ? ` (HTTP ${testResult.code})` : ""}
          </MessageBarBody>
        </MessageBar>
      ) : null}

      <Field label={i18n.url}>
        <Input value={url} placeholder={i18n.urlPlaceholder} onChange={(_, d) => setUrl(d.value)} />
      </Field>
      <Field label={i18n.events}>
        <div className={styles.events}>
          {WEBHOOK_EVENTS.map((event) => (
            <Checkbox
              key={event}
              label={event}
              checked={events.has(event)}
              onChange={(_, d) => toggle(event, Boolean(d.checked))}
            />
          ))}
        </div>
      </Field>
      <div>
        <Button
          appearance="primary"
          disabled={!url.trim() || events.size === 0 || create.isPending}
          onClick={() => create.mutate({ url: url.trim(), events: [...events] })}
        >
          {i18n.create}
        </Button>
      </div>

      {hooks.length === 0 ? (
        <MessageBar>
          <MessageBarBody>{i18n.empty}</MessageBarBody>
        </MessageBar>
      ) : (
        <Table aria-label={i18n.title}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>{i18n.columns.url}</TableHeaderCell>
              <TableHeaderCell>{i18n.columns.events}</TableHeaderCell>
              <TableHeaderCell>{i18n.columns.status}</TableHeaderCell>
              <TableHeaderCell>{i18n.columns.failures}</TableHeaderCell>
              <TableHeaderCell />
            </TableRow>
          </TableHeader>
          <TableBody>
            {hooks.map((hook: Webhook) => (
              <TableRow key={hook.id}>
                <TableCell className={styles.mono}>{hook.url}</TableCell>
                <TableCell>{hook.events.length}</TableCell>
                <TableCell>
                  <Badge
                    appearance="filled"
                    color={hook.status === "active" ? "success" : "informative"}
                  >
                    {i18n.statuses[hook.status]}
                  </Badge>
                </TableCell>
                <TableCell>{hook.failure_count}</TableCell>
                <TableCell>
                  <Button
                    appearance="subtle"
                    size="small"
                    disabled={test.isPending}
                    onClick={() => test.mutate(hook.id)}
                  >
                    {i18n.test}
                  </Button>
                  <Button
                    appearance="subtle"
                    size="small"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(hook.id)}
                  >
                    {i18n.delete}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )
}
